import ModuleContext from 'CMFW/context/ModuleContext';
import VocabularyModel, { IVoc, IVocTreeEdit } from 'models/VocabularyModel';
import React, { useContext, useMemo, useState } from 'react';
import styled from 'styled-components';
import { Tree } from 'react-organizational-chart';
import VocTreeNode from './VocTreeNode';
import VocTreeNodeItem from './VocTreeNodeItem';
import { Link } from '@material-ui/core';
import VocSearchModal from 'components/modal/VocSearchModal';
import { GenderPairContext } from './GenderPairContext';
import { AppContext } from 'AppContext';

const collectTreeIds = (node: IVocTreeEdit, acc: Set<number>): Set<number> => {
  acc.add(node.id);
  node.children.forEach((child) => collectTreeIds(child, acc));
  node.synonyms.forEach((synonym) => collectTreeIds(synonym, acc));
  return acc;
};

const findCurrentNode = (node: IVocTreeEdit): IVocTreeEdit | null => {
  if (node.current) {
    return node;
  }
  for (const child of node.children) {
    const found = findCurrentNode(child);
    if (found) {
      return found;
    }
  }
  for (const synonym of node.synonyms) {
    const found = findCurrentNode(synonym);
    if (found) {
      return found;
    }
  }
  return null;
};

const VocTree: React.FC = () => {
  const module = useContext(ModuleContext) as VocabularyModel;
  const app = useContext(AppContext);
  const moduleItemValue: IVocTreeEdit = module.getItemValue('tree');
  const parent: number | null = module.getItemValue('parent');
  const parentSynonym: number | null = module.getItemValue('parentSynonym');
  const genderBase: number | null = module.getItemValue('genderBase');
  const hasGenderDerived: boolean = module.getItemValue('hasGenderDerived');

  const [showModal, setShowModal] = useState<null | 'parent' | 'synonym' | 'genderBase'>(null);
  const [showChildModal, setShowChildModal] = useState<null | 'parent' | 'synonym'>(null);
  const [hoveredIds, setHoveredIds] = useState<number[] | null>(null);

  const treeIds = useMemo(() => collectTreeIds(moduleItemValue, new Set<number>()), [moduleItemValue]);
  const currentNode = useMemo(() => findCurrentNode(moduleItemValue), [moduleItemValue]);

  const genderPairCtxValue = useMemo(() => ({ treeIds, hoveredIds, setHoveredIds }), [treeIds, hoveredIds]);

  // The selected voc is part of a gender pair when it is the derived female (genderBase set)
  // or the male base (has a derived female). Either side shows the "Unlink gender" action.
  const currentInGenderPair = genderBase !== null || hasGenderDerived;

  const handleError = (message: string) => {
    app.setNotification(message);
  };

  const handleUnlinkChild = async (childId: number) => {
    await module.updateVocParent(childId, 0, handleError);
  };

  const handleUnlinkSynonym = async (synonymId: number) => {
    await module.updateVocParentSynonym(synonymId, 0, handleError);
  };

  const handleUnlinkSelf = async () => {
    await module.updateItemParent(0, handleError);
  };

  const onCloseModal = () => {
    setShowModal(null);
  };

  const onCloseChildModal = () => {
    setShowChildModal(null);
  };

  const handleParent = async () => {
    if (parent !== null) {
      await module.updateItemParent(0, handleError);
    } else {
      setShowModal('parent');
    }
  };

  const handleChild = async () => {
    setShowChildModal('parent');
  };

  const handleParentSynonym = async () => {
    if (parentSynonym !== null) {
      await module.updateItemParentSynonym(0, handleError);
    } else {
      setShowModal('synonym');
    }
  };

  const handleChildSynonym = async () => {
    setShowChildModal('synonym');
  };

  const handleLinkGender = () => {
    setShowModal('genderBase');
  };

  const handleUnlinkGender = async () => {
    if (genderBase !== null) {
      // Current voc is the derived female — it holds the gender base link.
      await module.updateItemGenderBase(0, handleError);
    } else if (currentNode?.genderPair != null) {
      // Current voc is the male base — the link lives on its derived female.
      await module.updateVocGenderBase(currentNode.genderPair.id, 0, handleError);
    }
  };

  const onChooseVoc = async (voc: IVoc) => {
    setShowModal(null);
    if (showModal === 'parent') {
      await module.updateItemParent(voc.id, handleError);
    }
    if (showModal === 'synonym') {
      await module.updateItemParentSynonym(voc.id, handleError);
    }
    if (showModal === 'genderBase') {
      await module.updateItemGenderBase(voc.id, handleError);
    }
  };

  const onChooseChildVoc = async (voc: IVoc) => {
    setShowChildModal(null);
    if (showChildModal === 'parent') {
      await module.updateVocParent(voc.id, module.itemId, handleError);
    }
    if (showChildModal === 'synonym') {
      await module.updateVocParentSynonym(voc.id, module.itemId, handleError);
    }
  };

  return (
    <Wrapper>
      {parentSynonym === null && (
        <LinkStyled onClick={handleParent}>{parent ? 'Unlink parent' : 'Link to parent'}</LinkStyled>
      )}
      {parentSynonym === null && <LinkStyled onClick={handleChild}>{'Link child'}</LinkStyled>}
      {parentSynonym === null &&
        parent === null &&
        moduleItemValue.children.length === 0 &&
        moduleItemValue.synonyms.length === 0 && (
          <LinkStyled onClick={handleParentSynonym}>{'Link to synonym'}</LinkStyled>
        )}
      {parentSynonym !== null && <LinkStyled onClick={handleParentSynonym}>{'Unlink to synonym'}</LinkStyled>}
      {parent === null && parentSynonym === null && (
        <LinkStyled onClick={handleChildSynonym}>{'Link synonym'}</LinkStyled>
      )}
      {currentInGenderPair ? (
        <LinkStyled onClick={handleUnlinkGender}>{'⚥ Unlink gender'}</LinkStyled>
      ) : (
        <LinkStyled onClick={handleLinkGender}>{'⚥ Link gender base'}</LinkStyled>
      )}
      <br />
      <br />
      <GenderPairContext.Provider value={genderPairCtxValue}>
        {(moduleItemValue.children.length > 0 || moduleItemValue.synonyms.length > 0) && (
          <Tree
            label={
              <VocTreeNodeItem
                item={moduleItemValue}
                onUnlinkChild={handleUnlinkChild}
                onUnlinkSynonym={handleUnlinkSynonym}
                onUnlinkSelf={handleUnlinkSelf}
                isRoot={true}
              />
            }
            lineWidth={'1px'}
            lineColor={'#C6C6CA'}
            lineBorderRadius={'4px'}
          >
            {moduleItemValue.children.map((child) => (
              <VocTreeNode
                item={child}
                key={child.id}
                onUnlinkChild={handleUnlinkChild}
                onUnlinkSynonym={handleUnlinkSynonym}
                onUnlinkSelf={handleUnlinkSelf}
                isRoot={false}
              />
            ))}
          </Tree>
        )}
      </GenderPairContext.Provider>
      {showModal !== null && <VocSearchModal onClose={onCloseModal} onChooseVoc={onChooseVoc} />}
      {showChildModal !== null && <VocSearchModal onClose={onCloseChildModal} onChooseVoc={onChooseChildVoc} />}
    </Wrapper>
  );
};

export default VocTree;

const Wrapper = styled.div``;

const LinkStyled = styled(Link)`
  padding: 4px 10px;
  font-size: 12px;
  cursor: pointer;
  align-self: flex-end;
`;
